import {
  StockBatchView,
  StockMeasure,
  requestedMilli,
  usableBatches,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import { MedicationStockFormValues } from './medicationStockValues';

/**
 * Where the "Z magazynu" section sits (F-7): a Medication's own taking; a
 * treatment drug line, which the backend always takes FEFO, so it has no
 * "Partia"; or a catalogue template's default, which takes nothing and
 * never names a recipient, so it has neither, nor a shortage warning.
 */
export type StockSectionVariant =
  | 'MEDICATION'
  | 'TREATMENT_LINE'
  | 'TEMPLATE_LINE';

/** Why FEFO would not offer a stored pin (notes-2 F-3). */
export type StalePinReason = 'EXPIRED' | 'EMPTY' | 'MISSING';

export interface PinnedBatchView<T extends StockBatchView> {
  id: string;
  /** Absent when the Product's batches do not list it (yet). */
  batch?: T;
  /** What the batch holds for this taking: its stock plus a saved taking pinned to it. */
  onHandMilli: number;
  stale: StalePinReason | null;
  /** Among the batches FEFO offers, so the select already lists it. */
  listed: boolean;
}

export interface MedicationStockView<T extends StockBatchView> {
  /**
   * The batches the vet may pin, in FEFO order; the first is what
   * "Automatycznie" takes. Each holds what it holds for this taking (see
   * `withSavedTaking`), so a label reads what the shortage check counts.
   */
  usable: T[];
  pinned?: PinnedBatchView<T>;
  availableMilli: number;
  requestedMilli: number;
  shortage: boolean;
}

export interface MedicationStockViewInput<T extends StockBatchView> {
  /** The picked Product's detail; nothing is measured before it loads. */
  product?: { onHandMilli: number; unitsPerPackage: number };
  batches: T[];
  /** Warsaw `YYYY-MM-DD` */
  today: string;
  productId: string;
  /** A watched text field holds the typed string (F-3). */
  quantity: number | string;
  measure: StockMeasure;
  batchId: string;
  /**
   * The form's default values. A saved taking there has already left the
   * Stock Level, so an edit counts it as available again instead of
   * subtracting it twice.
   */
  saved?: Partial<MedicationStockFormValues>;
}

/** Thousandths of a Unit the saved medication already took of `productId`. */
function savedMilli(
  saved: Partial<MedicationStockFormValues> | undefined,
  productId: string,
  unitsPerPackage: number,
): number {
  if (!saved?.stockWasSet || !productId || saved.stockProductId !== productId) {
    return 0;
  }
  return requestedMilli(
    saved.stockQuantity ?? '',
    saved.stockMeasure ?? 'UNIT',
    unitsPerPackage,
  );
}

/**
 * The batches as this taking sees them. A saved taking pinned to a batch
 * goes back to it before the edit takes again (the backend reverses first),
 * so that batch holds it too: in its label, as the pin, and in the shortage
 * check, whichever batch the vet picks now.
 */
function withSavedTaking<T extends StockBatchView>(
  batches: T[],
  savedBatchId: string | undefined,
  taken: number,
): T[] {
  if (!savedBatchId || taken <= 0) return batches;
  return batches.map((batch) =>
    batch.id === savedBatchId
      ? { ...batch, onHandMilli: batch.onHandMilli + taken }
      : batch,
  );
}

function pinnedView<T extends StockBatchView>(
  batches: T[],
  usable: T[],
  batchId: string,
  today: string,
): PinnedBatchView<T> {
  const batch = batches.find((item) => item.id === batchId);
  if (!batch) {
    return { id: batchId, onHandMilli: 0, stale: 'MISSING', listed: false };
  }
  const { onHandMilli } = batch;
  let stale: StalePinReason | null = null;
  if (batch.expiryDate < today) stale = 'EXPIRED';
  else if (onHandMilli <= 0) stale = 'EMPTY';
  return {
    id: batchId,
    batch,
    onHandMilli,
    stale,
    listed: usable.some((item) => item.id === batchId),
  };
}

/**
 * What the "Z magazynu" section shows about a taking: the batches, the pin
 * and whether the Stock Level covers the quantity. A FEFO taking's batches
 * are not known here, so a saved taking goes back to a pinned batch only
 * when it was pinned to that same batch.
 */
export function medicationStockView<T extends StockBatchView>({
  product,
  batches,
  today,
  productId,
  quantity,
  measure,
  batchId,
  saved,
}: MedicationStockViewInput<T>): MedicationStockView<T> {
  const unitsPerPackage = product?.unitsPerPackage ?? 1;
  const taken = savedMilli(saved, productId, unitsPerPackage);
  const seen = withSavedTaking(batches, saved?.stockBatchId, taken);
  const usable = usableBatches(seen, today);
  const pinned = batchId ? pinnedView(seen, usable, batchId, today) : undefined;
  const requested = product
    ? requestedMilli(quantity, measure, unitsPerPackage)
    : 0;
  const available = pinned
    ? pinned.onHandMilli
    : (product?.onHandMilli ?? 0) + taken;
  return {
    usable,
    pinned,
    availableMilli: available,
    requestedMilli: requested,
    shortage: Boolean(product) && requested > 0 && requested > available,
  };
}
